import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Radio, Users } from 'lucide-react';
import AdSlot from '@/components/AdSlot';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getShowImageSrc = (show) => {
  if (show.image_data && show.image_data.startsWith('data:')) return show.image_data;
  if (show.image_storage_path) return `${API}/show-image/${show.id}`;
  if (show.image_url && !show.image_url.startsWith('__storage__') && show.image_url.startsWith('http')) return show.image_url;
  return null;
};

const getHostImageSrc = (host) => {
  if (host.image_data && host.image_data.startsWith('data:')) return host.image_data;
  if (host.image_storage_path) return `${API}/host-image/${host.id}`;
  if (host.image_url && !host.image_url.startsWith('__storage__')) return host.image_url;
  return 'https://images.unsplash.com/photo-1581092800573-6afa755dcdc7?w=80';
};

export default function ShowsPage() {
  const [shows, setShows] = useState([]);
  const [hosts, setHosts] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/shows`),
      axios.get(`${API}/hosts`)
    ]).then(([sRes, hRes]) => {
      setShows(sRes.data.shows || []);
      setHosts(hRes.data.hosts || []);
    }).catch(() => {});
  }, []);

  const getHost = (hostId) => hosts.find(h => h.id === hostId);

  const getHostImageSrc = (host) => {
    if (host.image_storage_path) return `${API}/host-image/${host.id}`;
    return host.image_url || 'https://images.unsplash.com/photo-1581092800573-6afa755dcdc7?w=400';
  };

  return (
    <div data-testid="shows-page" className="pb-28 min-h-screen">
      {/* Header */}
      <div className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-3">Programs</p>
          <h1 className="text-4xl sm:text-5xl tracking-tighter font-semibold text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>
            Our Shows
          </h1>
        </div>
      </div>

      {/* Shows Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {shows.map((show, i) => {
            const host = getHost(show.host_id);
            return (
              <Link
                to={`/shows/${show.id}`}
                key={show.id}
                data-testid={`show-detail-${i}`}
                className="bg-[#171A21] rounded-2xl border border-white/5 shadow-2xl overflow-hidden hover:-translate-y-1 hover:border-[#D4AF37]/30 transition-all duration-300 group animate-fade-in-up block"
                style={{ animationDelay: `${i * 100}ms`, opacity: 0 }}
              >
                <div className="h-44 bg-gradient-to-br from-[#D4AF37]/15 to-[#991B1B]/15 flex items-center justify-center relative overflow-hidden">
                  {getShowImageSrc(show) ? (
                    <img src={getShowImageSrc(show)} alt={show.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <Radio size={56} className="text-[#D4AF37]/40 group-hover:text-[#D4AF37]/60 transition-colors" />
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-white font-semibold text-xl mb-2">{show.name}</h3>
                  <p className="text-[#94A3B8] text-sm line-clamp-3 mb-4">{show.description}</p>
                  {host && host.name && (
                    <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                      <img
                        src={getHostImageSrc(host)}
                        alt={host.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-white text-sm font-medium">{host.name}</p>
                        <p className="text-[#94A3B8] text-xs">Host</p>
                      </div>
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>

        <AdSlot slotKey="shows" page="/shows" />

        {/* Hosts Section */}
        {hosts.length > 0 && (
          <>
            <div className="mb-10">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-3">Meet the Team</p>
              <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                Our Hosts & DJs
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {hosts.map((host, i) => (
                <div
                  key={host.id}
                  data-testid={`host-card-${i}`}
                  className="bg-[#171A21] rounded-2xl border border-white/5 overflow-hidden hover:-translate-y-1 transition-transform duration-300"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={getHostImageSrc(host)}
                      alt={host.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-white font-semibold text-lg mb-2">{host.name}</h3>
                    <p className="text-[#94A3B8] text-sm line-clamp-3">{host.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
